import {
  Component,
  inject,
  input,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
  WritableSignal,
  signal,
  InputSignal,
  ChangeDetectorRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  AbstractControl,
  ControlContainer,
  FormBuilder,
  FormControl,
  FormGroup,
  FormGroupDirective,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';
import { CalendarComponent } from '../date-picker-components/calendar/calendar.component';
import { fromEvent } from 'rxjs';
import { FormInputComponent } from '../../form-input.component';
import { ErrorMessageComponent } from '../../../../layout/error-message/error-message.component';
import { AppendTestIdPipe } from '../../../../../pipes/append-test-id/append-test-id.pipe';
import { PopupPosition } from '../../../../../directives/popup-position';

interface IDateForm {
  day: FormControl<string>;
  month: FormControl<string>;
  year: FormControl<string>;
  hour: FormControl<string>;
  minutes: FormControl<string>;
}

@Component({
  selector: 'aw-date-input',
  viewProviders: [
    {
      provide: ControlContainer,
      useExisting: FormGroupDirective,
    },
  ],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ErrorMessageComponent,
    AppendTestIdPipe,
    CalendarComponent,
    PopupPosition,
  ],
  templateUrl: './date-input.component.html',
  encapsulation: ViewEncapsulation.None,
})
export class DateInputComponent
  extends FormInputComponent
  implements OnInit, OnDestroy
{
  public readonly showTimePicker: InputSignal<boolean> = input(false);
  public readonly max: InputSignal<Date | undefined> = input();
  public readonly min: InputSignal<Date | undefined> = input();
  public readonly restrictedDateList: InputSignal<Date[] | null> = input<
    Date[] | null
  >(null);

  private readonly formBuilder: FormBuilder = inject(FormBuilder);
  private readonly cdRef: ChangeDetectorRef = inject(ChangeDetectorRef);

  public readonly showCalendar: WritableSignal<boolean> = signal(false);

  protected readonly innerControl: FormControl<string> =
    this.formBuilder.nonNullable.control<string>('');

  protected readonly dateForm: FormGroup<IDateForm> = this.formBuilder.group({
    day: this.formBuilder.nonNullable.control(''),
    month: this.formBuilder.nonNullable.control(''),
    year: this.formBuilder.nonNullable.control(''),
    hour: this.formBuilder.nonNullable.control(''),
    minutes: this.formBuilder.nonNullable.control(''),
  });

  public ngOnInit(): void {
    this.ngControl.control!.addValidators(this.isDateWithinBounds());
    if (this.ngControl.value) {
      this.onDateChange(new Date(this.ngControl.value), false);
      this.cdRef.detectChanges();
    }
    this.subscription.add(
      this.ngControl.control!.valueChanges.subscribe(date => {
        if (date) {
          this.dateForm.setValue(
            {
              day: this.cleanDay(date.getDate() + ''),
              month: this.cleanMonth(date.getMonth() + 1 + ''),
              year: date.getFullYear() + '',
              hour: this.cleanHours(date.getHours() + ''),
              minutes: this.cleanMinutes(date.getMinutes() + ''),
            },
            { emitEvent: false }
          );
        } else {
          this.dateForm.reset(
            {
              day: '',
              month: '',
              year: '',
              hour: '',
              minutes: '',
            },
            { emitEvent: false }
          );
        }
        this.innerControl.setValue(this.formatDate());
        this.cdRef.detectChanges();
      })
    );
    this.subscription.add(
      this.dateForm.controls.day.valueChanges.subscribe((value: string) => {
        let cleanedValue: string = this.cleanNumber(value);
        if (value.length > 2) {
          cleanedValue = value[0] + value[1];
        }
        if (+cleanedValue > 31) {
          cleanedValue = '31';
        }
        this.dateForm.controls.day.setValue(cleanedValue, {
          emitEvent: false,
        });
      })
    );
    this.subscription.add(
      this.dateForm.controls.month.valueChanges.subscribe((value: string) => {
        let cleanedValue: string = this.cleanNumber(value);
        if (value.length > 2) {
          cleanedValue = value[0] + value[1];
        }
        if (+cleanedValue > 12) {
          cleanedValue = '12';
        }
        this.dateForm.controls.month.setValue(cleanedValue, {
          emitEvent: false,
        });
      })
    );
    this.subscription.add(
      this.dateForm.controls.year.valueChanges.subscribe((value: string) => {
        let cleanedValue: string = this.cleanNumber(value);
        if (value.length > 4) {
          // if more than this is needed we have different problems
          cleanedValue = value[0] + value[1] + value[2] + value[3];
        }
        this.dateForm.controls.year.setValue(cleanedValue, {
          emitEvent: false,
        });
      })
    );
    this.subscription.add(
      this.dateForm.controls.hour.valueChanges.subscribe((value: string) => {
        let cleanedValue: string = this.cleanNumber(value);
        if (value.length > 2) {
          cleanedValue = value[0] + value[1];
        }
        if (+cleanedValue > 23) {
          cleanedValue = '23';
        }
        this.dateForm.controls.hour.setValue(cleanedValue, {
          emitEvent: false,
        });
      })
    );
    this.subscription.add(
      this.dateForm.controls.minutes.valueChanges.subscribe((value: string) => {
        let cleanedValue: string = this.cleanNumber(value);
        if (value.length > 2) {
          cleanedValue = value[0] + value[1];
        }
        if (+cleanedValue > 59) {
          cleanedValue = '59';
        }
        this.dateForm.controls.minutes.setValue(cleanedValue, {
          emitEvent: false,
        });
      })
    );
    this.subscription.add(
      this.dateForm.valueChanges.subscribe(dateForm => {
        this.setDate(
          dateForm.day,
          dateForm.month,
          dateForm.year,
          dateForm.hour,
          dateForm.minutes
        );
      })
    );
    this.subscription.add(
      fromEvent<KeyboardEvent & { target?: { id: string } }>(
        document,
        'keyup'
      ).subscribe(event => {
        const isCorrectTarget = event.target?.id?.includes(this.id());
        if (isCorrectTarget) {
          event.preventDefault();
          if (event.key === ' ') {
            this.showCalendar.set(true);
          }
        } else if (!isCorrectTarget && this.showCalendar()) {
          event.preventDefault();
          this.showCalendar.set(false);
          this.ngControl.control!.markAsTouched();
        }
        if (event.key === 'Escape' && this.showCalendar()) {
          this.showCalendar.set(false);
          this.ngControl.control!.markAsTouched();
        }
      })
    );
    this.subscription.add(
      fromEvent<MouseEvent & { target?: { id: string } }>(
        document,
        'mouseup'
      ).subscribe(event => {
        const isCorrectTarget = event?.target?.id?.includes(this.id());
        if (!isCorrectTarget && this.showCalendar()) {
          this.showCalendar.set(false);
          this.ngControl.control!.markAsTouched();
        }
      })
    );
  }

  override ngOnDestroy() {
    super.ngOnDestroy();
    this.ngControl.control!.removeValidators(this.isDateWithinBounds());
  }

  public formatDate(): string {
    const date = this.dateForm.value;
    if (!date.year || !date.month || !date.day) {
      return '';
    }
    const dateString = `${date.year}-${date.month}-${date.day}`;
    return this.showTimePicker()
      ? dateString + ` ${date.hour}:${date.minutes}`
      : dateString;
  }

  protected setDateFromInnerControl(): void {
    const wasEdited: boolean = this.innerControl.dirty;
    const date: Date = new Date(this.innerControl.value);
    if (!date || !(date instanceof Date) || isNaN(date.getDate())) {
      this.dateForm.reset();
      this.ngControl.control!.setValue(undefined);
      this.setDate();
    } else {
      this.dateForm.controls.day.setValue(
        this.cleanDay(date.getDate().toString())
      );
      this.dateForm.controls.month.setValue(
        this.cleanMonth((date.getMonth() + 1).toString())
      );
      this.dateForm.controls.year.setValue(date.getFullYear().toString());
      if (this.showTimePicker()) {
        this.dateForm.controls.hour.setValue(
          this.cleanHours(date.getHours().toString())
        );
        this.dateForm.controls.minutes.setValue(
          this.cleanMinutes(date.getMinutes().toString())
        );
      }
    }
    if (wasEdited) {
      this.ngControl.control!.markAsDirty();
      this.ngControl.control!.markAsTouched();
      this.innerControl.markAsPristine();
    }
  }

  protected setDate(
    day?: string,
    month?: string,
    year?: string,
    hour: string = '00',
    minutes: string = '00'
  ): void {
    this.innerControl.setValue(this.formatDate());
    if (day && month && year) {
      this.ngControl.control!.setValue(
        new Date(`${month}.${day}.${year} ${hour}:${minutes}`)
      );
      this.ngControl.control!.updateValueAndValidity();
    }
  }

  protected reset(): void {
    this.ngControl.control!.setValue(null);
    this.ngControl.control!.markAsDirty();
    this.ngControl.control!.markAsTouched();
    this.dateForm.reset();
    this.showCalendar.set(false);
  }

  protected onDateChange(date: Date, markAsDirty: boolean = true): void {
    this.ngControl.control!.setValue(date);
    this.dateForm.setValue({
      day: this.cleanDay(date.getDate() + ''),
      month: this.cleanMonth(date.getMonth() + 1 + ''),
      year: date.getFullYear() + '',
      hour: this.cleanHours(date.getHours() + ''),
      minutes: this.cleanMinutes(date.getMinutes() + ''),
    });
    if (markAsDirty) {
      this.ngControl.control!.markAsDirty();
    }
    this.ngControl.control!.markAsTouched();
    this.innerControl.setValue(this.formatDate());
    this.showCalendar.set(false);
  }

  protected cleanNumber(value: string): string {
    const isNumber = new RegExp('^[0-9]*$');
    return value
      .split('')
      .filter(character => isNumber.test(character))
      .join('');
  }

  protected cleanDay(value: string | undefined): string {
    if (!value) {
      return '01';
    }
    let cleanedValue: string = this.cleanNumber(value);
    if (+cleanedValue > 31) {
      cleanedValue = '01';
    }
    if (+cleanedValue < 10 && +cleanedValue > 0 && cleanedValue.length < 2) {
      cleanedValue = '0' + cleanedValue;
    } else if (+cleanedValue < 1) {
      cleanedValue = '01';
    }
    return cleanedValue;
  }

  protected cleanMonth(value: string | undefined): string {
    if (!value) {
      return '01';
    }
    let cleanedValue: string = this.cleanNumber(value);
    if (+cleanedValue > 12) {
      cleanedValue = '01';
    }
    if (+cleanedValue < 10 && +cleanedValue > 0 && cleanedValue.length < 2) {
      cleanedValue = '0' + cleanedValue;
    } else if (+cleanedValue < 1) {
      cleanedValue = '01';
    }
    return cleanedValue;
  }

  protected cleanHours(value: string | undefined): string {
    if (!value) {
      return '00';
    }
    let cleanedValue: string = this.cleanNumber(value);
    if (+cleanedValue > 23) {
      cleanedValue = '00';
    }
    if (+cleanedValue < 10 && +cleanedValue > 0 && cleanedValue.length < 2) {
      cleanedValue = '0' + cleanedValue;
    } else if (+cleanedValue < 1) {
      cleanedValue = '00';
    }
    return cleanedValue;
  }

  protected cleanMinutes(value: string | undefined): string {
    if (!value) {
      return '00';
    }
    let cleanedValue: string = this.cleanNumber(value);
    if (+cleanedValue > 59) {
      cleanedValue = '00';
    }
    if (+cleanedValue < 10 && +cleanedValue >= 1 && cleanedValue.length < 2) {
      cleanedValue = '0' + cleanedValue;
    } else if (+cleanedValue < 1) {
      cleanedValue = '00';
    }
    return cleanedValue;
  }

  protected closeCalendar(): void {
    this.showCalendar.set(false);
  }

  private isDateWithinBounds(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (
        control.value === undefined ||
        control.value === null ||
        control.value === ''
      ) {
        return null;
      }

      const chosenDate: Date = control.value;
      const minDate: Date | undefined = this.min();
      const maxDate: Date | undefined = this.max();
      const listDates: Date[] | null = this.restrictedDateList();

      const chosenDateTime: number = this.sanitizedTimeStamp(chosenDate);

      if (minDate) {
        const minDateTime: number = this.sanitizedTimeStamp(minDate);
        return chosenDateTime < minDateTime ? { dateMin: true } : null;
      }

      if (maxDate) {
        const maxDateTime: number = this.sanitizedTimeStamp(maxDate);
        return chosenDateTime > maxDateTime ? { dateMax: true } : null;
      }

      if (listDates) {
        return !listDates.some(
          (date: Date) => this.sanitizedTimeStamp(date) === chosenDateTime
        )
          ? { dateNotInList: true }
          : null;
      }

      return null;
    };
  }

  private sanitizedTimeStamp(date: Date): number {
    const sanitizedDate = new Date(date);
    sanitizedDate.setHours(0, 0, 0, 0);
    return sanitizedDate.getTime();
  }
}
